import { useMemo } from "react";
import { matchPath } from "react-router";

import { useFeatures } from "~api/features";
import { usePermitted } from "~auth/usePermitted.ts";
import { useCurrentEmployee } from "~contexts/CurrentEmployeeContext/CurrentEmployeeContext";
import type {
  HiddenRoute,
  NavigationDefinitions,
  NavigationGroup,
  NavigationItem,
  RouteWithProps,
} from "./navigationDefinitions";
import { hiddenRoutes, navigationDefinitions } from "./navigationDefinitions";

export function useNavigationDefinitions(
  definitions: NavigationGroup[] = navigationDefinitions,
  hidden: (RouteWithProps | HiddenRoute)[] = hiddenRoutes,
) {
  const permitted = usePermitted();
  const { currentEmployee } = useCurrentEmployee();
  const groups: NavigationDefinitions = useMemo(
    () =>
      definitions
        .map((group) => ({
          ...group,
          items: group.items.filter((item) =>
            shouldShowItem(
              item,
              currentEmployee?.beta_user,
              permitted(item.route.permissions),
            ),
          ),
        }))
        .filter((group) => group.items.length > 0),
    [currentEmployee?.beta_user, permitted, definitions],
  );

  const hiddenRts = hidden.filter((item) => permitted(item.route.permissions));

  return {
    navigationDefinitions: groups,
    hiddenRoutes: hiddenRts,
  };
}

export function isActivePath(pathname: string, path: string): boolean {
  return Boolean(matchPath({ path, end: false }, pathname));
}

function shouldShowItem(
  item: NavigationItem,
  betaUser: boolean | undefined,
  permitted: boolean,
) {
  if (item.disabled) {
    return false;
  }
  if (!permitted) {
    return false;
  }

  if (!betaUser) {
    return !item.betaFeature;
  }

  return true;
}

export function useEnabledNavigationDefinitions(
  definitions: NavigationGroup[] = navigationDefinitions,
  hidden: (RouteWithProps | HiddenRoute)[] = hiddenRoutes,
) {
  const { navigationDefinitions: allNavigationDefinitions } =
    useNavigationDefinitions(definitions, hidden);
  const { data: features, isLoading, isPlaceholderData } = useFeatures();

  const groups: NavigationDefinitions = useMemo(
    () =>
      allNavigationDefinitions
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => {
            if (item.featureToggle) {
              // A tenant switch briefly serves the previous tenant's
              // features as a placeholder (see useFeatures) - hide the item
              // rather than deciding on the wrong tenant's flag.
              if (isPlaceholderData) {
                return false;
              }

              const feature = (features || []).find(
                (f) => f.name === item.featureToggle,
              );

              if (!feature?.enabled) {
                return false;
              }
            }

            return true;
          }),
        }))
        .filter((group) => group.items.length > 0),
    [features, allNavigationDefinitions, isPlaceholderData],
  );

  return { navigationDefinitions: groups, isLoading };
}
